import { useEffect, useMemo } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Polyline, Polygon, Marker, Tooltip, Rectangle, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import { pathM, fmtM, vallasFor } from "@/lib/geo";
import { KINDS, DEFAULT_CENTER } from "@/lib/constants";
import { VertexEditor } from "@/components/map/VertexEditor";
import { OneWayLayer, arrows, arrowIcon } from "@/components/map/OneWayLayer";
import { signIcon } from "@/components/map/SignObjects";
import { StaffMarkers } from "@/components/map/StaffMarkers";
import { groupOf } from "@/components/map/MapLayersPanel";

const PNOA = "https://www.ign.es/wmts/pnoa-ma?service=WMTS&request=GetTile&version=1.0.0&format=image/jpeg&layer=OI.OrthoimageCoverage&style=default&tilematrixset=GoogleMapsCompatible&tilematrix={z}&tilerow={y}&tilecol={x}";
const PNOA_ATTR = 'Ortofoto <a href="https://pnoa.ign.es">PNOA</a> © IGN España';

const postIcon = (n) => L.divIcon({ className: "", html: `<div class="tac-marker sel" style="width:24px;height:24px;background:#0ea5e9;color:#0ea5e9"><span style="color:#0b1329;font-weight:800">${n}</span></div>`, iconSize: [24, 24], iconAnchor: [12, 12] });

const FlyTo = ({ target }) => {
  const map = useMap();
  useEffect(() => { if (target?.loc) map.flyTo(target.loc, Math.max(map.getZoom(), 18), { duration: 0.6 }); }, [target, map]);
  return null;
};

const CenterOn = ({ center, hasContent }) => {
  const map = useMap();
  useEffect(() => { if (center && !hasContent) map.setView(center, 16); }, [center?.[0], center?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const Resizer = ({ trigger }) => {
  const map = useMap();
  useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 120); return () => clearTimeout(t); }, [trigger, map]);
  return null;
};

const OSM = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
export const NoEntryMini = () => (
  <svg width="12" height="12" viewBox="0 0 22 22" className="shrink-0"><circle cx="11" cy="11" r="10" fill="#dc2626" stroke="#fff" strokeWidth="1.5" /><rect x="4.5" y="9" width="13" height="4" fill="#fff" /></svg>
);
const ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const icon = (color, label, sel, size = 24, residents = false) =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative"><div class="tac-marker ${sel ? "sel" : ""}" style="width:${size}px;height:${size}px;background:${color};color:${color}"><span style="color:#fff">${label}</span></div>${residents ? resTag : ""}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

const resTag = '<span style="position:absolute;bottom:-9px;left:50%;transform:translateX(-50%);background:#fff;color:#0f172a;font:700 7px/9px sans-serif;padding:0 2px;border-radius:2px;border:1px solid #0f172a">RES</span>';

const noEntryIcon = (accepted, residents, sel) => L.divIcon({
  className: "",
  html: `<div style="position:relative;width:22px;height:22px;opacity:${accepted ? 1 : 0.6};${sel ? "filter:drop-shadow(0 0 6px #38bdf8)" : "filter:drop-shadow(0 0 2px #000)"}">
    <svg width="22" height="22" viewBox="0 0 22 22"><circle cx="11" cy="11" r="10" fill="#dc2626" stroke="#fff" stroke-width="1.5" ${accepted ? "" : 'stroke-dasharray="3 2"'}/><rect x="4.5" y="9" width="13" height="4" rx="0.5" fill="#fff"/></svg>
    ${residents ? resTag : ""}</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const ClickHandler = ({ onClick }) => {
  useMapEvents({ click: (e) => onClick && onClick([+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6)]) });
  return null;
};

const FitBounds = ({ elements, detours, trigger }) => {
  const map = useMap();
  useEffect(() => {
    const pts = [...elements.flatMap((e) => e.coords || []), ...(detours || []).flatMap((d) => d.coords || [])];
    const t = setTimeout(() => {
      map.invalidateSize();
      if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40], maxZoom: 17 });
      else if (pts.length === 1) map.setView(pts[0], 17);
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return null;
};

const FocusSelected = ({ el }) => {
  const map = useMap();
  useEffect(() => {
    if (!el?.coords?.length) return;
    const b = L.latLngBounds(el.coords);
    const size = map.latLngToContainerPoint(b.getNorthEast()).distanceTo(map.latLngToContainerPoint(b.getSouthWest()));
    if (el.coords.length > 1 && size < 120) map.flyToBounds(b, { padding: [80, 80], maxZoom: 18, duration: 0.6 });
    else if (!map.getBounds().contains(b)) map.flyTo(el.coords[0], Math.max(map.getZoom(), 17), { duration: 0.6 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el?.id]);
  return null;
};

const ElementShape = ({ el, idx, selected, onSelect, timing, draggable, onElementChange, onPass }) => {
  const color = KINDS[el.kind]?.color;
  // while drawing/assigning, a click on a shape acts as a click on the map at that point
  const handlers = { click: (e) => { L.DomEvent.stopPropagation(e); if (onPass) onPass([+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6)]); else if (onSelect) onSelect(el.id); } };
  const tip = (
    <Tooltip direction="top" sticky>
      <div className="text-xs font-semibold">{el.name || KINDS[el.kind].short}</div>
      {timing && <div className="text-[0.6875rem]">{timing.start_hm} – {timing.end_hm}</div>}
      {el.auto_generated && <div className="text-[0.625rem]">Corte previo · {el.locked ? "aceptado" : "propuesta pendiente"}</div>}
      {el.residents_only && <div className="text-[0.625rem]">Excepto residentes</div>}
      {draggable && (el.kind === "cut_point" || el.kind === "sign") && <div className="text-[0.625rem]">Arrastre para mover</div>}
    </Tooltip>
  );
  if (!el.coords?.length) return null;
  if (el.kind === "sign" && el.sign_type === "valla_linea" && el.coords.length > 1)
    return (
      <>
        <Polyline positions={el.coords} pathOptions={{ color: "#fff", weight: selected ? 9 : 7, opacity: 0.95 }} eventHandlers={handlers} />
        <Polyline positions={el.coords} pathOptions={{ color: "#dc2626", weight: selected ? 9 : 7, dashArray: "6 6" }} eventHandlers={handlers}>{tip}</Polyline>
        <Marker position={el.coords[Math.floor(el.coords.length / 2)]} icon={signIcon(el, selected)} eventHandlers={handlers} />
      </>
    );
  if (el.kind === "sign")
    return (
      <Marker position={el.coords[0]} icon={signIcon(el, selected)} draggable={draggable}
        eventHandlers={{ ...handlers, dragend: (e) => { const p = e.target.getLatLng(); onElementChange({ ...el, coords: [[+p.lat.toFixed(6), +p.lng.toFixed(6)]] }); } }}>{tip}</Marker>
    );
  if (el.kind === "cut_point")
    return (
      <Marker position={el.coords[0]} icon={el.auto_generated ? noEntryIcon(el.locked, el.residents_only, selected) : icon(color, idx, selected, 24, el.residents_only)} draggable={draggable}
        eventHandlers={{ ...handlers, dragend: (e) => { const p = e.target.getLatLng(); onElementChange({ ...el, coords: [[+p.lat.toFixed(6), +p.lng.toFixed(6)]], ...(el.auto_generated ? { locked: true } : {}) }); } }}>{tip}</Marker>
    );
  if (el.kind === "no_parking")
    return (
      <Polygon positions={el.coords} eventHandlers={handlers}
        pathOptions={{ color, weight: selected ? 3 : 2, fillOpacity: 0.25, dashArray: "6 4" }}>{tip}</Polygon>
    );
  return (
    <>
      <Polyline positions={el.coords} eventHandlers={handlers}
        pathOptions={{ color, weight: selected ? 9 : el.kind === "route" ? 6 : el.kind === "special_path" ? 4 : el.kind === "reversed_oneway" ? 6 : 7, opacity: 0.9, dashArray: el.kind === "street_cut" ? "2 10" : el.kind === "special_path" ? "12 6" : el.kind === "reversed_oneway" ? "14 5" : null, lineCap: "round" }}>{tip}</Polyline>
      {el.kind === "reversed_oneway" && arrows(el.coords, 45).map((a, i) => <Marker key={`ra-${i}`} position={a.p} icon={arrowIcon(a.deg, color)} interactive={false} />)}
      {el.kind === "route" && <Marker position={el.coords[0]} icon={icon(color, "S", false, 20)} interactive={false} zIndexOffset={-500} />}
      {el.kind === "route" && el.coords.length > 1 && <Marker position={el.coords[el.coords.length - 1]} icon={icon("#334155", "F", false, 20)} interactive={false} zIndexOffset={-500} />}
    </>
  );
};

const detourArrow = (deg, pending) => L.divIcon({
  className: "",
  html: `<svg width="18" height="18" viewBox="0 0 14 14" style="transform:rotate(${deg}deg);opacity:${pending ? 0.55 : 1}"><path d="M7 1 L12.5 12 L7 9 L1.5 12 Z" fill="#15803d" stroke="#fff" stroke-width="1.4" stroke-linejoin="round"/></svg>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

export default function EventMap({ elements, detours = [], timings = {}, selectedId, onSelect, drawMode, draft = [], onMapClick, light, height = "100%", fitTrigger = 0, interactive = true, onElementChange, showOneWays, onOneWayStatus, onPickOneWay, base = "dark", hidden = [], resizeKey, measure = [], box = [], center, staff = [], onSelectStaff, selectedStaff, onMoveStaff, onSelectDetour, selectedDetour, onDetourChange, posts = [], focus }) {
  const numbered = useMemo(() => {
    let n = 0;
    return elements.map((e) => ({ el: e, idx: e.kind === "cut_point" && !e.auto_generated ? ++n : null })).filter(({ el }) => !hidden.includes(groupOf(el)));
  }, [elements, hidden]);
  if (hidden.includes("detours")) detours = [];
  // cada desvío puede traer ramas: el tráfico que llega al corte por otras calles, con su propio itinerario
  const lines = detours.flatMap((d) => [{ ...d, key: d.element_id }, ...(d.branches || []).map((b, i) => ({ ...d, ...b, key: `${d.element_id}-r${i}`, branch: true, manual: false, entry_point: b.coords[0] }))]);

  return (
    <div className={`${drawMode ? "draw-cursor " : ""}h-full isolate`} style={{ height }}>
      <MapContainer center={center || DEFAULT_CENTER} zoom={16} maxZoom={22} style={{ height: "100%", width: "100%" }} zoomControl={interactive}
        dragging={interactive} scrollWheelZoom={interactive} doubleClickZoom={false} attributionControl>
        {base === "pnoa" && !light
          ? <TileLayer key="pnoa" url={PNOA} attribution={PNOA_ATTR} maxZoom={22} maxNativeZoom={20} />
          : <TileLayer key={light || base === "light" ? "light" : "dark"} url={OSM} attribution={ATTR} className={light ? "print-tiles" : base === "light" ? "" : "tactical-tiles"} maxZoom={22} maxNativeZoom={19} />}
        <Resizer trigger={resizeKey} />
        <CenterOn center={center} hasContent={elements.length > 0} />
        <FlyTo target={focus} />
        {posts.length > 1 && <Polyline positions={posts.map((p) => p.loc)} pathOptions={{ color: "#38bdf8", weight: 2, dashArray: "6 6" }} interactive={false} />}
        {posts.map((p, i) => <Marker key={`post-${p.id}`} position={p.loc} icon={postIcon(i + 1)} interactive={false} zIndexOffset={1000} />)}
        {!hidden.includes("staff") && <StaffMarkers points={staff} onSelect={onSelectStaff} selectedKey={selectedStaff} onMove={onMoveStaff} />}
        <FitBounds elements={elements} detours={detours} trigger={fitTrigger} />
        {interactive && <ClickHandler onClick={drawMode ? onMapClick : () => onSelect && onSelect(null)} />}
        {showOneWays && <OneWayLayer onStatus={onOneWayStatus} onPick={drawMode === "reversed_oneway" && !draft.length ? onPickOneWay : undefined} />}
        {lines.filter((d) => d.coords?.length).map((d) => (
          <Polyline key={`d-${d.key}-${d.coords.length}`} positions={d.coords}
            eventHandlers={onSelectDetour ? { click: (e) => { L.DomEvent.stopPropagation(e); if (drawMode) onMapClick([+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6)]); else onSelectDetour(d.element_id); } } : undefined}
            pathOptions={{ color: d.manual ? "#4ade80" : d.status === "ok" || d.pending ? "#22c55e" : "#a3e635", weight: d.element_id === selectedDetour ? 11 : d.pending ? 5 : 8, opacity: d.element_id === selectedDetour ? 0.9 : d.pending ? 0.55 : 0.6, dashArray: d.manual ? "2 8" : "10 8" }}>
            <Tooltip sticky><div className="text-xs font-semibold">Desvío · {d.element_name}{d.pending ? " (propuesto: acepte el corte previo)" : ""}</div>{d.from && <div className="text-[0.6875rem]">Tráfico desde {d.from}</div>}<div className="text-[0.6875rem]">{d.streets.join(" → ")}</div></Tooltip>
          </Polyline>
        ))}
        {/* flechas en el sentido en que circula el tráfico desviado (el trazado va de la entrada a la salida) */}
        {lines.filter((d) => d.coords?.length > 1).flatMap((d) => arrows(d.coords, 45).map((a, i) => (
          <Marker key={`da-${d.key}-${i}`} position={a.p} icon={detourArrow(a.deg, d.pending)} interactive={false} />
        )))}
        {detours.flatMap((d) => (d.oneway_conflicts || []).map((c, i) => (
          <Polyline key={`oc-${d.element_id}-${i}`} positions={c.coords} pathOptions={{ color: "#ff1f3d", weight: 9, opacity: 0.95, dashArray: "4 6" }}>
            <Tooltip sticky><div className="text-xs font-semibold">Contra sentido · {c.name}</div><div className="text-[0.6875rem]">Desvío {d.element_name} · {c.length_m} m</div></Tooltip>
          </Polyline>
        )))}
        {lines.filter((d) => d.coords?.length).map((d) => (
          <Marker key={`de-${d.key}`} position={d.entry_point} icon={icon("#16a34a", "D", false, 20)}>
            <Tooltip direction="top"><div className="text-xs">Inicio desvío · {d.element_name}{d.from ? ` · desde ${d.from}` : ""}</div></Tooltip>
          </Marker>
        ))}
        {numbered.map(({ el, idx }) => (
          <ElementShape key={el.id} el={el} idx={idx} selected={el.id === selectedId} onSelect={onSelect} timing={timings[el.id]}
            draggable={!!onElementChange && !drawMode && el.id === selectedId} onElementChange={onElementChange} onPass={drawMode ? onMapClick : undefined} />
        ))}
        {onElementChange && !drawMode && <VertexEditor el={elements.find((e) => e.id === selectedId)} onChange={onElementChange} />}
        {onDetourChange && !drawMode && (() => {
          const d = detours.find((x) => x.element_id === selectedDetour && x.manual);
          return d ? <VertexEditor el={{ id: `det-${d.element_id}`, kind: "detour", coords: d.coords }} onChange={(n) => onDetourChange(d.element_id, n.coords)} /> : null;
        })()}
        {onElementChange && <FocusSelected el={elements.find((e) => e.id === selectedId)} />}
        {draft.length > 0 && (
          drawMode === "no_parking"
            ? <Polygon positions={draft} pathOptions={{ color: "#38bdf8", dashArray: "4 4", fillOpacity: 0.15 }} />
            : <Polyline positions={draft} pathOptions={{ color: "#38bdf8", dashArray: "4 4", weight: 4 }} />
        )}
        {draft.map((p, i) => <Marker key={`dr-${i}`} position={p} icon={icon("#38bdf8", i + 1, false, 16)} />)}
        {measure.length > 1 && <Polyline positions={measure} pathOptions={{ color: "#f8fafc", weight: 3, dashArray: "6 6" }} interactive={false} />}
        {measure.map((p, i) => (
          <CircleMarker key={`ms-${i}`} center={p} radius={4} pathOptions={{ color: "#0f172a", weight: 2, fillColor: "#f8fafc", fillOpacity: 1 }} interactive={false}>
            {i === measure.length - 1 && i > 0 && <Tooltip permanent direction="right" offset={[8, 0]}><span className="text-xs font-semibold">{fmtM(pathM(measure))} · {vallasFor(pathM(measure))} vallas</span></Tooltip>}
          </CircleMarker>
        ))}
        {box.length === 2 && <Rectangle bounds={box} pathOptions={{ color: "#fbbf24", weight: 2, dashArray: "6 4", fillOpacity: 0.08 }} interactive={false} />}
        {box.length === 1 && <CircleMarker center={box[0]} radius={5} pathOptions={{ color: "#fbbf24", fillColor: "#fbbf24", fillOpacity: 1 }} interactive={false} />}
      </MapContainer>
    </div>
  );
}
